import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import {
  NdaTemplateDocumentReferenceStore,
  TermSheetStore,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  AccessLevel,
  DocumentAttribute,
  DocumentMeta,
  DocumentMetaDataType,
  DocumentReferenceEntityType,
  DocumentType,
} from '@qtek/shared/models';
import { buildDocumentReferenceFormData } from '@qtek/shared/utils';
import { BehaviorSubject, filter, map, race, take, timer } from 'rxjs';
import {
  applyCcaExtraction,
  CcaExtractionResult,
} from './cca-extraction.utils';

export const closedDealEntryMethodDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'closed-deal-entry-method-dialog'],
  width: '600px',
  maxWidth: '95vw',
};

const CCA_EXTRACTION_TIMER = 180000; // 180s — CAs are the largest docs; covers the BE per-attempt Bedrock timeout (BEDROCK_CALL_TIMEOUT=180s) + queue slack. Term sheets use 120s (current-deal-entry-method-dialog); CA was left at 60s and lost the race on big docs (SD-2185).

@Component({
  selector: 'qt-closed-deal-entry-method-dialog',
  templateUrl: './closed-deal-entry-method-dialog.component.html',
  styleUrls: ['./closed-deal-entry-method-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslationCoreModule,
  ],
})
export class ClosedDealEntryMethodDialogComponent implements OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly ndaTemplateStore = inject(NdaTemplateDocumentReferenceStore);
  private termSheetStore = inject(TermSheetStore);
  private readonly docMeta$ = toObservable(this.termSheetStore.docMeta);
  private dialogRef = inject(
    MatDialogRef<ClosedDealEntryMethodDialogComponent>
  );
  private dialogLoader = inject(LazyDialogLoader);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  /** 'select' = card selection, 'upload' = file drop zone, 'processing' = extraction in progress */
  phase = signal<'select' | 'upload' | 'processing'>('select');
  dragCounter = 0;
  fileList: File[] = [];
  isUploading$ = new BehaviorSubject(false);
  currentUploadMessage$ = new BehaviorSubject('Uploading document...');
  uploadError = signal(false);

  private createdTemplateId: string | null = null;
  private ccaMysid: string | null = null;
  private uploadMessageTimer: any;

  private uploadMessages = [
    'Uploading document...',
    'Document uploaded successfully',
    'Processing credit agreement...',
    'Extracting deal fields...',
  ];

  // ─── Card Actions ─────────────────────────────────────

  selectManualEntry(): void {
    this.dialogRef.close();
    this.dialogLoader.open('closed-deal-stepper').subscribe();
  }

  selectUploadCA(): void {
    this.phase.set('upload');
  }

  goBack(): void {
    const currentPhase = this.phase();
    if (currentPhase === 'upload') {
      this.cleanupUploadState();
      this.phase.set('select');
    } else if (currentPhase === 'select') {
      this.dialogRef.close();
      this.dialogLoader.open('select-deal-type').subscribe();
    }
  }

  // ─── File Upload ──────────────────────────────────────

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.uploadError.set(false);
      this.fileList = Array.from(input.files);
      if (this.fileList.length > 0) {
        this.startUploadProcess();
      }
    }
  }

  handleDrop(event: DragEvent): void {
    this.dragCounter = 0;
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.uploadError.set(false);
      this.fileList = Array.from(files);
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      if (this.fileList.length > 0) {
        this.startUploadProcess();
      }
    }
  }

  dragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter--;
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
  }

  // ─── Upload & Extraction ──────────────────────────────

  private startUploadProcess(): void {
    if (this.fileList.length === 0) return;
    this.phase.set('processing');
    this.startUploadMessageCycling();
    this.uploadCcaDocument();
  }

  private uploadCcaDocument(): void {
    this.isUploading$.next(true);
    this.uploadError.set(false);

    const file = this.fileList[0];
    const formData = buildDocumentReferenceFormData({
      file,
      fileName: file.name,
      entityType: DocumentReferenceEntityType.NDA_TEMPLATE,
      // v2 equivalent of v1's `_ks: 'ndaTemplate={{<<crud-insert>>}}'` — create the parent
      // NdaTemplate as part of the upload; the id comes back as `entityId`.
      entityJson: '{}',
      attribute: DocumentAttribute.COMPANY_NDA,
      accessLevel: AccessLevel.SHARED,
      description: file.name,
      // The user chose "credit agreement" to get here, so the type is declared rather than
      // inferred. DocumentClassifier leaves a settled type alone, which keeps an LLM call
      // off the stepper's critical path and out of its timeout budget (SD-2745).
      documentType: DocumentType.CREDIT_AGREEMENT,
    });

    this.ndaTemplateStore
      .upload(formData)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: reference => {
          // v2 returns the created parent's id directly; v1 required parsing it out of a
          // `ndaTemplate=<id>` refId string.
          if (reference?.entityId) {
            this.createdTemplateId = reference.entityId;
          }
          // Subscribe to DocMeta immediately on upload success to avoid race condition
          // with the Kafka worker extraction completing before the subscription is active.
          // The document id is nested under the reference in v2.
          const documentId = reference?.document?._id;
          if (documentId) {
            this.subscribeToDocMeta(documentId);
          } else {
            this.uploadError.set(true);
            this.stopUploadMessageCycling();
            this.isUploading$.next(false);
            this.phase.set('upload');
          }
        },
        error: () => {
          this.uploadError.set(true);
          this.stopUploadMessageCycling();
          this.isUploading$.next(false);
          this.phase.set('upload');
        },
      });
  }

  private subscribeToDocMeta(docId: string): void {
    this.ccaMysid = `cca_${docId}_${Date.now()}`;
    const mysid = this.ccaMysid;

    this.termSheetStore.connect({
      mysid,
      documentId: docId,
      dataType: DocumentMetaDataType.CREDIT_AGREEMENT,
    });

    const docMetaResponse$ = this.docMeta$.pipe(
      map(docMeta => docMeta[mysid]),
      filter(Boolean),
      take(1)
    );

    const timeout$ = timer(CCA_EXTRACTION_TIMER);

    race(docMetaResponse$, timeout$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.stopUploadMessageCycling();
        this.isUploading$.next(false);

        if (value && typeof value === 'object') {
          this.handleExtractionSuccess(value as DocumentMeta, docId);
        } else {
          this.openStepperWithData(null, docId);
        }
      });
  }

  private handleExtractionSuccess(response: DocumentMeta, docId: string): void {
    const containers = response?.containers;
    if (!containers?.length || !containers[0]?.data) {
      this.openStepperWithData(null, docId);
      return;
    }

    // containers[0] = parent deal, containers[1..N] = sub-facilities
    const additionalContainers =
      containers.length > 1
        ? containers
            .slice(1)
            .map((c: any) => c.data)
            .filter(Boolean)
        : undefined;

    const result = applyCcaExtraction(containers[0].data, additionalContainers);
    this.openStepperWithData(result, docId);
  }

  private openStepperWithData(
    extractionResult: CcaExtractionResult | null,
    docId: string
  ): void {
    this.dialogRef.close();
    this.dialogLoader
      // SD-2150: pass the uploaded docId so the stepper links it to the new
      // deal (data-room visibility + covenant extraction discoverability).
      .open('closed-deal-stepper', { extractionResult, docId })
      .subscribe();
  }

  // ─── Message Cycling ──────────────────────────────────

  private startUploadMessageCycling(): void {
    this.currentUploadMessage$.next(this.uploadMessages[0]);
    let currentIndex = 0;
    const interval = CCA_EXTRACTION_TIMER / this.uploadMessages.length;

    this.uploadMessageTimer = setInterval(() => {
      currentIndex++;
      if (currentIndex < this.uploadMessages.length) {
        this.currentUploadMessage$.next(this.uploadMessages[currentIndex]);
      } else {
        this.stopUploadMessageCycling();
      }
    }, interval);
  }

  private stopUploadMessageCycling(): void {
    if (this.uploadMessageTimer) {
      clearInterval(this.uploadMessageTimer);
      this.uploadMessageTimer = null;
    }
  }

  // ─── Cleanup ──────────────────────────────────────────

  private cleanupUploadState(): void {
    this.stopUploadMessageCycling();
    this.fileList = [];
    this.createdTemplateId = null;
    this.isUploading$.next(false);
    this.uploadError.set(false);

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    if (this.ccaMysid) {
      this.termSheetStore.disconnect(this.ccaMysid);
      this.ccaMysid = null;
    }
  }

  ngOnDestroy(): void {
    this.cleanupUploadState();
    this.isUploading$.complete();
    this.currentUploadMessage$.complete();
  }
}
